import { useState, useEffect, useCallback } from "react";
import api, { apiErr, apiGet } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Archive, RotateCcw, ChevronDown, SlidersHorizontal, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { PROFILE_GROUPS, ITEM_DEFS, OBSERVATION_FIELDS, newObservationDraft, lbl } from "./config";
import { inputCls, asArray, HelpCtx, useHelpForm, FieldHelp, FieldRow, readValue, hasVal, StickyActions } from "./fields";

// ---- Profile section (read-first / edit-second) ----
const ProfileSection = ({ section, data, planData, linkOptions, onSave }) => {
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const help = useHelpForm();
  const start = () => { setDraft({ ...(data || {}) }); help.setOpenKey(null); setEdit(true); };
  const save = async () => { setBusy(true); try { await onSave(section.key, draft); setEdit(false); } catch (e) { /* upstream */ } finally { setBusy(false); } };
  const shownFields = section.fields.filter((f) => hasVal(f, (data || {})[f.key]));

  const derived = section.derived ? (planData || {}) : null;
  const derivedPriorities = derived ? asArray(derived.current_priorities).map((r) => r.text).filter(Boolean) : [];
  const derivedNext = derived ? asArray(derived.next_steps).map((r) => r.text).filter(Boolean) : [];

  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={`section-${section.key}`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-head text-base font-bold">{section.title}</h4>
        {!edit && <Button size="sm" variant="outline" className="rounded-full" data-testid={`edit-${section.key}`} onClick={start}><Pencil className="h-3.5 w-3.5 mr-1" />Modifica</Button>}
      </div>
      <HelpCtx.Provider value={help}>
        {!edit ? (
          (shownFields.length === 0 && derivedPriorities.length === 0 && derivedNext.length === 0)
            ? <p className="text-sm text-muted-foreground">Nessuna informazione registrata.</p>
            : (
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                {shownFields.map((f) => (
                  <div key={f.key} className={f.type === "area" || f.type === "rep" ? "sm:col-span-2" : ""}>
                    <div className="text-muted-foreground text-xs">{f.label}</div>
                    <div className="font-medium break-words">{readValue(f, (data || {})[f.key], linkOptions)}</div>
                  </div>
                ))}
                {derived && <DerivedSummary priorities={derivedPriorities} next={derivedNext} />}
              </div>
            )
        ) : (
          <div className="space-y-3">
            {section.fields.map((f) => (
              <FieldRow key={f.key} f={f} value={draft[f.key]} options={linkOptions} onChange={(v) => setDraft((d) => ({ ...d, [f.key]: v }))} />
            ))}
            {section.derived && <p className="text-xs text-muted-foreground">Priorità e prossimi passi si gestiscono nel «Piano di lavoro attuale».</p>}
            <StickyActions>
              <Button size="sm" variant="ghost" onClick={() => setEdit(false)}>Annulla</Button>
              <Button size="sm" className="rounded-full" data-testid={`save-${section.key}`} disabled={busy} onClick={save}>{busy ? "Salvataggio…" : "Salva"}</Button>
            </StickyActions>
          </div>
        )}
      </HelpCtx.Provider>
    </div>
  );
};

const DerivedSummary = ({ priorities, next }) => (
  <>
    <div className="sm:col-span-2"><div className="text-muted-foreground text-xs">Priorità attuali (dal Piano di lavoro)</div>
      <div className="font-medium">{priorities.length ? <ul className="list-disc pl-4">{priorities.map((p, i) => <li key={i}>{p}</li>)}</ul> : "—"}</div></div>
    <div className="sm:col-span-2"><div className="text-muted-foreground text-xs">Prossimo passo (dal Piano di lavoro)</div>
      <div className="font-medium">{next.length ? <ul className="list-disc pl-4">{next.map((p, i) => <li key={i}>{p}</li>)}</ul> : "—"}</div></div>
  </>
);

// ---- Item add/edit dialog ----
export const ItemDialog = ({ type, item, dogId, linkOptions, onClose, onSaved }) => {
  const def = ITEM_DEFS[type];
  const help = useHelpForm();
  const [title, setTitle] = useState(item?.title || "");
  const [payload, setPayload] = useState(() => ({ ...(item?.payload || {}) }));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!title.trim()) return toast.error("Il titolo è obbligatorio.");
    for (const f of def.fields) {
      if (f.required && f.type === "enum" && !payload[f.key]) return toast.error(`Seleziona: ${f.label}`);
      if (f.required && (f.type === "area" || f.type === "text") && !String(payload[f.key] || "").trim()) return toast.error(`Compila: ${f.label}`);
    }
    const clean = Object.fromEntries(Object.entries(payload).filter(([, v]) => !(v === "" || v === undefined || v === null || (Array.isArray(v) && v.length === 0))));
    setBusy(true);
    try {
      if (item) await api.put(`/professional-items/${item.id}`, { title, payload: clean, record_version: item.record_version });
      else await api.post(`/dogs/${dogId}/professional-items`, { item_type: type, title, payload: clean });
      toast.success("Salvato"); onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="item-dialog" className="max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{item ? "Modifica" : "Aggiungi"} {def.singular}</DialogTitle></DialogHeader>
        <HelpCtx.Provider value={help}>
          <div className="space-y-3">
            <div>
              <div className="text-sm font-medium flex items-center flex-wrap">{def.titleLabel || "Titolo"}<span className="text-destructive ml-0.5">*</span>
                <FieldHelp fkey="__title" help={def.titleHelp} example={def.titleExample} /></div>
              <input data-testid="item-title" className={`${inputCls} mt-1`} value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            {def.fields.map((f) => (
              <FieldRow key={f.key} f={f} value={payload[f.key]} options={linkOptions} onChange={(v) => setPayload((p) => ({ ...p, [f.key]: v }))} />
            ))}
          </div>
          <StickyActions>
            <Button variant="ghost" onClick={onClose}>Annulla</Button>
            <Button className="rounded-full" data-testid="item-save" disabled={busy} onClick={save}>{busy ? "…" : "Salva"}</Button>
          </StickyActions>
        </HelpCtx.Provider>
      </DialogContent>
    </Dialog>
  );
};

// ---- Trigger observations dialog ----
const ObsRow = ({ o }) => (
  <div className="rounded-xl border border-border p-3 text-sm" data-testid={`obs-row-${o.id}`}>
    <div className="flex justify-between gap-2 flex-wrap"><span className="font-semibold">{fmtDate(o.observation_date)} · {lbl("threshold_type", o.threshold_type)}</span><span className="text-xs text-muted-foreground">Diseng.: {lbl("disengagement", o.disengagement)}</span></div>
    {o.threshold_value_text && <div className="break-words mt-0.5">{o.threshold_value_text}</div>}
    {o.source_type && <div className="text-xs text-muted-foreground mt-0.5">Fonte: {lbl("source", o.source_type)} · Precisione: {lbl("precision", o.precision)}</div>}
    {o.context && <div className="text-xs mt-1 whitespace-pre-line"><b>Contesto:</b> {o.context}</div>}
    {o.early_signals && <div className="text-xs mt-1 whitespace-pre-line"><b>Segnali precoci:</b> {o.early_signals}</div>}
    {o.observed_response && <div className="text-xs mt-1 whitespace-pre-line"><b>Risposta:</b> {o.observed_response}</div>}
    {o.recovery && <div className="text-xs mt-1 whitespace-pre-line"><b>Recupero:</b> {o.recovery}</div>}
    {o.notes && <div className="text-muted-foreground text-xs mt-1 whitespace-pre-line">{o.notes}</div>}
  </div>
);

export const ObservationsDialog = ({ trigger, onClose }) => {
  const [obs, setObs] = useState([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(newObservationDraft);
  const help = useHelpForm();
  const load = useCallback(() => { apiGet(`/professional-items/${trigger.id}/observations`).then(({ data }) => setObs(data)).catch(() => {}); }, [trigger.id]);
  useEffect(() => { load(); }, [load]);
  const startAdd = () => { setDraft(newObservationDraft()); help.setOpenKey(null); setAdding(true); };
  const add = async () => {
    for (const f of OBSERVATION_FIELDS) {
      if (f.required && f.type === "enum" && !draft[f.key]) return toast.error(`Seleziona: ${f.label}`);
    }
    const clean = Object.fromEntries(Object.entries(draft).filter(([, v]) => !(v === "" || v === undefined || v === null)));
    try { await api.post(`/professional-items/${trigger.id}/observations`, clean); toast.success("Osservazione aggiunta"); setAdding(false); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="obs-dialog" className="max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Soglie · storico · {trigger.title}</DialogTitle></DialogHeader>
        {!adding && <Button size="sm" className="rounded-full" data-testid="obs-add" onClick={startAdd}><Plus className="h-4 w-4 mr-1" />Aggiungi soglia / osservazione</Button>}
        {adding && (
          <HelpCtx.Provider value={help}>
            <div className="space-y-3 border border-border rounded-xl p-3">
              <div>
                <div className="text-sm font-medium">Data</div>
                <input data-testid="obs-date" type="date" className={`${inputCls} mt-1`} value={draft.observation_date} onChange={(e) => setDraft((d) => ({ ...d, observation_date: e.target.value }))} />
              </div>
              {OBSERVATION_FIELDS.map((f) => (
                <FieldRow key={f.key} f={f} value={draft[f.key]} onChange={(v) => setDraft((d) => ({ ...d, [f.key]: v }))} />
              ))}
              <StickyActions>
                <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Annulla</Button>
                <Button size="sm" className="rounded-full" data-testid="obs-save" onClick={add}>Salva</Button>
              </StickyActions>
            </div>
          </HelpCtx.Provider>
        )}
        <div className="space-y-2 mt-2" data-testid="obs-history">
          {obs.length === 0 ? <p className="text-sm text-muted-foreground">Nessuna osservazione registrata.</p> : obs.map((o) => <ObsRow key={o.id} o={o} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const summaryOf = (def, it) => {
  for (const f of def.fields) {
    if (f.type === "area" || f.type === "text") { const v = it.payload?.[f.key]; if (v && String(v).trim()) return String(v); }
    if (f.type === "enum" && it.payload?.[f.key]) return lbl(f.group, it.payload[f.key]);
  }
  return "";
};

const ItemActions = ({ def, it, isArch, onEdit, onObs, onArchive, onRestore }) => (
  <div className="flex flex-wrap gap-2 pt-2">
    {def.hasObservations && !isArch && <Button size="sm" variant="outline" className="rounded-full" data-testid={`item-obs-${it.id}`} onClick={() => onObs(it)}><SlidersHorizontal className="h-3.5 w-3.5 mr-1" />Soglie / Storico</Button>}
    {!isArch && <Button size="sm" variant="outline" className="rounded-full" data-testid={`item-edit-${it.id}`} onClick={() => onEdit(it)}><Pencil className="h-3.5 w-3.5 mr-1" />Modifica</Button>}
    {isArch
      ? <Button size="sm" variant="outline" className="rounded-full" data-testid={`item-restore-${it.id}`} onClick={() => onRestore(it.id)}><RotateCcw className="h-3.5 w-3.5 mr-1" />Ripristina</Button>
      : <AlertDialog>
          <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="rounded-full text-muted-foreground" data-testid={`item-archive-${it.id}`}><Archive className="h-3.5 w-3.5 mr-1" />Archivia</Button></AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>Archiviare questa voce?</AlertDialogTitle>
              <AlertDialogDescription>La voce viene archiviata mantenendo tutta la sua storia. Potrai ripristinarla.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction data-testid={`item-archive-confirm-${it.id}`} onClick={() => onArchive(it.id)}>Archivia</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>}
  </div>
);

const ItemCard = ({ type, it, isArch, def, linkOptions, allItems, onEdit, onObs, onArchive, onRestore }) => {
  const [open, setOpen] = useState(false);
  const summary = summaryOf(def, it);
  const derivedStrategies = type === "objective"
    ? allItems.filter((s) => s.item_type === "strategy" && asArray(s.payload?.linked_objective_ids).includes(it.id)).map((s) => s.title)
    : [];
  return (
    <div className="rounded-xl border border-border bg-card p-3" data-testid={`item-card-${it.id}`}>
      <button type="button" className="w-full text-left" data-testid={`item-toggle-${it.id}`} onClick={() => setOpen((v) => !v)}>
        <div className="flex items-start gap-1.5 min-w-0">
          <ChevronDown className={`h-4 w-4 mt-0.5 shrink-0 text-muted-foreground transition-transform ${open ? "" : "-rotate-90"}`} />
          <div className="min-w-0">
            <div className="font-semibold break-words flex items-center gap-1.5">
              {type === "risk" && it.payload?.show_safety_alert && !isArch && <ShieldAlert className="h-4 w-4 text-[#C62828] shrink-0" />}
              {it.title}
            </div>
            <div className="text-xs text-muted-foreground">{lbl(def.statusGroup, it.status)}{it.updated_at ? ` · agg. ${fmtDate((it.updated_at || "").slice(0, 10))}` : ""}</div>
            {!open && summary && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1 break-words">{summary}</div>}
          </div>
        </div>
      </button>
      {open && (
        <div className="mt-2 pt-2 border-t border-border space-y-1.5 text-sm" data-testid={`item-detail-${it.id}`}>
          {def.fields.map((f) => {
            const rendered = readValue(f, it.payload?.[f.key], linkOptions);
            if (rendered === null) return null;
            return <div key={f.key}><span className="text-muted-foreground text-xs">{f.label}: </span><span className="font-medium break-words">{rendered}</span></div>;
          })}
          {type === "objective" && derivedStrategies.length > 0 && (
            <div><span className="text-muted-foreground text-xs">Strategie collegate: </span><span className="font-medium">{derivedStrategies.join(", ")}</span></div>
          )}
          <ItemActions def={def} it={it} isArch={isArch} onEdit={onEdit} onObs={onObs} onArchive={onArchive} onRestore={onRestore} />
        </div>
      )}
    </div>
  );
};

const ItemGroup = ({ type, items, allItems, dogId, linkOptions, onChanged }) => {
  const def = ITEM_DEFS[type];
  const [dlg, setDlg] = useState(null);
  const [obsFor, setObsFor] = useState(null);
  const active = items.filter((i) => !i.archived_at);
  const archived = items.filter((i) => i.archived_at);
  const archive = async (id) => { try { await api.post(`/professional-items/${id}/archive`); toast.success("Archiviata"); onChanged(); } catch (e) { toast.error(apiErr(e)); } };
  const restore = async (id) => { try { await api.post(`/professional-items/${id}/restore`); toast.success("Ripristinata"); onChanged(); } catch (e) { toast.error(apiErr(e)); } };
  const cardProps = { type, def, linkOptions, allItems, onEdit: (it) => setDlg({ item: it }), onObs: setObsFor, onArchive: archive, onRestore: restore };
  return (
    <div data-testid={`itemgroup-${type}`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-head text-base font-bold">{def.title}</h4>
        <Button size="sm" className="rounded-full" data-testid={`add-${type}`} onClick={() => setDlg({})}><Plus className="h-4 w-4 mr-1" />Aggiungi</Button>
      </div>
      {active.length === 0 ? <p className="text-sm text-muted-foreground mb-2">{def.empty}</p> : <div className="space-y-2">{active.map((it) => <ItemCard key={it.id} it={it} {...cardProps} />)}</div>}
      {archived.length > 0 && (
        <details className="mt-2"><summary className="text-sm text-muted-foreground cursor-pointer font-medium" data-testid={`archived-summary-${type}`}>Archiviate ({archived.length})</summary>
          <div className="space-y-2 mt-2">{archived.map((it) => <ItemCard key={it.id} it={it} isArch {...cardProps} />)}</div></details>
      )}
      {dlg && <ItemDialog type={type} item={dlg.item} dogId={dogId} linkOptions={linkOptions} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); onChanged(); }} />}
      {obsFor && <ObservationsDialog trigger={obsFor} onClose={() => setObsFor(null)} />}
    </div>
  );
};

const GROUP_ITEMS = {
  profilo: ["motivation", "trigger", "risk", "resource"],
  percorso: ["hypothesis", "objective", "strategy"],
};

export const ProfessionalRecord = ({ dogId, onRisks }) => {
  const [profile, setProfile] = useState(null);
  const [items, setItems] = useState([]);
  const loadProfile = useCallback(() => { apiGet(`/dogs/${dogId}/professional-profile`).then(({ data }) => setProfile(data)).catch(() => {}); }, [dogId]);
  const loadItems = useCallback(() => {
    apiGet(`/dogs/${dogId}/professional-items`).then(({ data }) => {
      setItems(data);
      if (onRisks) onRisks(data.filter((i) => i.item_type === "risk" && !i.archived_at && i.payload?.show_safety_alert && i.status === "active"));
    }).catch(() => {});
  }, [dogId, onRisks]);
  useEffect(() => { loadProfile(); loadItems(); }, [loadProfile, loadItems]);

  const saveSection = async (key, value) => {
    try {
      const body = { record_version: profile?.record_version || 0 };
      for (const g of PROFILE_GROUPS) for (const s of g.sections) body[s.key] = s.key === key ? value : (profile?.[s.key] || {});
      const { data } = await api.put(`/dogs/${dogId}/professional-profile`, body);
      setProfile({ ...data, exists: true }); toast.success("Scheda aggiornata");
    } catch (e) { toast.error(apiErr(e)); throw e; }
  };

  const itemsOf = (t) => items.filter((i) => i.item_type === t);
  const activeOpts = (t) => itemsOf(t).filter((i) => !i.archived_at).map((i) => ({ id: i.id, title: i.title }));
  const linkOptions = { trigger: activeOpts("trigger"), risk: activeOpts("risk"), objective: activeOpts("objective"), strategy: activeOpts("strategy") };
  if (!profile) return <p className="text-sm text-muted-foreground">Caricamento…</p>;

  return (
    <div className="space-y-4" data-testid="professional-record">
      {PROFILE_GROUPS.map((g, gi) => (
        <CollapsibleSection key={g.id} id={`prof-${g.id}`} testid={`prof-group-${g.id}`} defaultOpen={gi === 0} title={g.title} titleClass="font-head text-lg font-bold">
          <div className="space-y-3">
            {g.sections.map((s) => <ProfileSection key={s.key} section={s} data={profile[s.key]} planData={profile.current_plan} linkOptions={linkOptions} onSave={saveSection} />)}
            {(GROUP_ITEMS[g.id] || []).map((t) => (
              <ItemGroup key={t} type={t} items={itemsOf(t)} allItems={items} dogId={dogId} linkOptions={linkOptions} onChanged={loadItems} />
            ))}
          </div>
        </CollapsibleSection>
      ))}
    </div>
  );
};
